"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.debug = exports.createLog = exports.removeLogMutater = exports.removeLogListener = exports.prefixMutater = exports.addLogMutater = exports.addLogListener = exports.LogListenerMode = exports.LogLevel = void 0;
const debug_1 = __importDefault(require("debug"));
/** The level of a log message */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["Debug"] = 0] = "Debug";
    LogLevel[LogLevel["Warning"] = 1] = "Warning";
    LogLevel[LogLevel["Error"] = 2] = "Error";
})(LogLevel || (exports.LogLevel = LogLevel = {}));
// Making this a global variable should get around having multiple instances of
// this package installed. They just all have to conform to the same namespace
// so any future changes should be backwards-compatible.
if (!global.logListeners) {
    ;
    global.logListeners = [];
}
if (!global.logMutaters) {
    ;
    global.logMutaters = [];
}
var LogListenerMode;
(function (LogListenerMode) {
    LogListenerMode[LogListenerMode["FilteredDebug"] = 0] = "FilteredDebug";
    LogListenerMode[LogListenerMode["AllDebugWithColors"] = 1] = "AllDebugWithColors";
    LogListenerMode[LogListenerMode["AllDebugWithoutColors"] = 2] = "AllDebugWithoutColors";
})(LogListenerMode || (exports.LogListenerMode = LogListenerMode = {}));
/**
 * Adds a new log listener.
 *
 * @remarks
 * This is used, for example, in the Sentry feature to manually create breadcrumbs for any
 * feature log messages (so we don't have to have the DEBUG environment variable enabled in
 * production environments).
 *
 * @param identifier - the identifier for the listener, used to remove it later.
 * @param callback - the listener callback, called when a message is sent to the logger.
 * @param mode - what mode you want to listen to debug logs in.
 */
const addLogListener = (identifier, callback, mode) => {
    if (!global.logListeners.some((listener) => listener.identifier === identifier)) {
        ;
        global.logListeners.push({
            identifier,
            callback,
            mode,
        });
    }
};
exports.addLogListener = addLogListener;
/**
 * Adds a new log mutater.
 *
 * @remarks
 * This is used, for example, in feature-server for tagging all Lambda logs with the
 * current API Gateway request ID, if available.
 *
 * @param identifier - the identifier for the mutater, used to remove it later.
 * @param callback - the mutater callback, called when a message is sent to the logger.
 */
const addLogMutater = (identifier, callback) => {
    if (!global.logMutaters.some((mutater) => mutater.identifier === identifier)) {
        ;
        global.logMutaters.push({
            identifier,
            callback,
        });
    }
};
exports.addLogMutater = addLogMutater;
/**
 * Creates a log mutater that prefixes all log entries.
 *
 * @param prefix - the prefix to add to the log entries
 * @returns the mutater callback to pass to addLogMutater()
 */
const prefixMutater = (prefix) => (level, args) => {
    if (typeof args[0] === 'string') {
        return [`${prefix}\t${args[0]}`, ...args.slice(1)];
    }
    else {
        return [prefix, ...args];
    }
};
exports.prefixMutater = prefixMutater;
/**
 * Removes an existing log listener.
 * @param identifier - the identifier of the listener
 */
const removeLogListener = (identifier) => {
    ;
    global.logListeners = global.logListeners.filter((listener) => listener.identifier !== identifier);
};
exports.removeLogListener = removeLogListener;
/**
 * Removes an existing log mutater.
 * @param identifier - the identifier of the mutater
 */
const removeLogMutater = (identifier) => {
    ;
    global.logMutaters = global.logMutaters.filter((mutater) => mutater.identifier !== identifier);
};
exports.removeLogMutater = removeLogMutater;
const callLogListeners = (level, args, namespace, mode) => {
    for (const listener of global.logListeners) {
        if (mode === undefined || listener.mode === mode) {
            listener.callback(level, args, namespace);
        }
    }
};
const callLogMutaters = (level, args) => {
    let runningArgs = args;
    for (const mutater of global.logMutaters) {
        runningArgs = mutater.callback(level, runningArgs);
    }
    return runningArgs;
};
/**
 * Creates a new log instance.
 *
 * @remarks
 * Logging should never be done with `console.log`, and should always be done through this
 * `createLog` function. This way, we can control the visibility of messages in different
 * environments and can listen for messages to send them other places (perhaps a centralized
 * logging service).
 *
 * @example
 * ```ts
 * const { debug, error, warn } = createLog('feature-users')
 * debug('boom boom test message with a %s variable', 'cool')
 * ```
 *
 * @param namespace - the namespace of the log. Typically the name of the feature.
 */
const createLog = (namespace) => {
    const db = (0, debug_1.default)(namespace);
    const allDb = (0, debug_1.default)(namespace);
    const noColorAllDb = (0, debug_1.default)(namespace);
    allDb.useColors = true;
    noColorAllDb.useColors = false;
    allDb.enabled = true;
    noColorAllDb.enabled = true;
    db.log = (...args) => {
        console.log(...args);
        callLogListeners(LogLevel.Debug, args, namespace, LogListenerMode.FilteredDebug);
    };
    allDb.log = (...args) => {
        callLogListeners(LogLevel.Debug, args, namespace, LogListenerMode.AllDebugWithColors);
    };
    noColorAllDb.log = (...args) => {
        callLogListeners(LogLevel.Debug, args, namespace, LogListenerMode.AllDebugWithoutColors);
    };
    const debug = (message, ...args) => {
        const realArgs = callLogMutaters(LogLevel.Debug, [message, ...args]);
        db(realArgs[0], ...realArgs.slice(1));
        allDb(realArgs[0], ...realArgs.slice(1));
        noColorAllDb(realArgs[0], ...realArgs.slice(1));
    };
    const log = (...args) => {
        const realArgs = callLogMutaters(LogLevel.Debug, args);
        console.log(...realArgs);
        callLogListeners(LogLevel.Debug, realArgs, namespace);
    };
    const error = (...args) => {
        const realArgs = callLogMutaters(LogLevel.Error, args);
        console.error(...realArgs);
        callLogListeners(LogLevel.Error, realArgs, namespace);
    };
    const warn = (...args) => {
        const realArgs = callLogMutaters(LogLevel.Warning, args);
        console.warn(...realArgs);
        callLogListeners(LogLevel.Warning, realArgs, namespace);
    };
    return { debug, log, error, warn };
};
exports.createLog = createLog;
var debug_2 = require("debug");
Object.defineProperty(exports, "debug", { enumerable: true, get: function () { return __importDefault(debug_2).default; } });
//# sourceMappingURL=index.js.map